/**
 * CollisionSystem - Centralized collision handling
 * 
 * Delegates collision events to appropriate systems.
 * Makes collision logic easier to debug and extend.
 * 
 * @module systems/CollisionSystem
 */

import eventBus, { GameEvents } from '../core/EventBus.js';

export default class CollisionSystem {
    /**
     * @param {Phaser.Scene} scene - The game scene
     */
    constructor(scene) {
        this.scene = scene;
        this.systems = [];
        this.levelBuilder = null;
        this.player = null;
        this.isGameOver = false;
        
        // Callbacks
        this.onPlayerDeath = null;
        this.onStarCollect = null;
        this.onGoalReached = null;
        this.onEnemyKill = null;
        this.onButtonPress = null;
    }

    /**
     * Initialize collision system
     * @param {Object} options - Configuration options
     */
    init(options) {
        this.player = options.player;
        this.levelBuilder = options.levelBuilder;
        this.systems = options.systems || [];
        this.enemies = options.enemies || [];
        this.doorOpen = false;
        
        this.onPlayerDeath = options.onPlayerDeath;
        this.onStarCollect = options.onStarCollect;
        this.onGoalReached = options.onGoalReached;
        this.onEnemyKill = options.onEnemyKill;
        this.onButtonPress = options.onButtonPress;

        // Register Matter.js collision events
        this.scene.matter.world.on('collisionstart', this.handleCollisionStart, this);
        this.scene.matter.world.on('collisionactive', this.handleCollisionActive, this);
    }

    /**
     * Handle collision start events
     * @param {Object} event - Collision event
     */
    handleCollisionStart(event) {
        if (this.isGameOver) return;

        event.pairs.forEach((pair) => {
            this.processPair(pair);
        });
    }

    /**
     * Handle active collision events (continuous contact)
     * @param {Object} event - Collision event
     */
    handleCollisionActive(event) {
        if (this.isGameOver) return;

        event.pairs.forEach((pair) => {
            const bodyA = pair.bodyA;
            const bodyB = pair.bodyB;
            
            // Check for box on button
            const isBoxA = bodyA.label === 'box' || bodyA.label === 'magneticBox';
            const isBoxB = bodyB.label === 'box' || bodyB.label === 'magneticBox';
            
            if ((bodyA.label === 'button' && isBoxB) || (bodyB.label === 'button' && isBoxA)) {
                if (this.onButtonPress && !this.doorOpen) {
                    this.doorOpen = true;
                    this.onButtonPress();
                }
            }
        });
    }

    /**
     * Process a collision pair
     * @param {Object} pair - Collision pair
     */
    processPair(pair) {
        const bodyA = pair.bodyA;
        const bodyB = pair.bodyB;

        // Check if player is involved
        if (bodyA.label !== 'player' && bodyB.label !== 'player') {
            return;
        }

        const playerBody = bodyA.label === 'player' ? bodyA : bodyB;
        const other = bodyA.label === 'player' ? bodyB : bodyA;

        // Delegate to systems first
        for (const system of this.systems) {
            if (system.ownsBody && system.ownsBody(other)) {
                const handled = system.handleCollision(other, this.player);
                if (handled) {
                    // Some systems signal death by returning true
                    if (other.label === 'lava' || other.label === 'spike' || other.label === 'asteroid') {
                        this.triggerPlayerDeath();
                    }
                    return;
                }
            }
        }

        // Handle built-in collision types
        switch (other.label) {
            case 'enemy':
                this.handleEnemyCollision(playerBody, other);
                break;

            case 'star':
                this.handleStarCollision(other);
                break;

            case 'goal':
                if (this.doorOpen && this.onGoalReached) {
                    this.onGoalReached();
                }
                break;
        }
    }

    /**
     * Handle enemy collision
     * @param {Object} playerBody - Player physics body
     * @param {Object} enemyBody - Enemy physics body
     */
    handleEnemyCollision(playerBody, enemyBody) {
        // Player kills enemy if landing from above
        if (playerBody.position.y < enemyBody.position.y - 15) {
            this.killEnemy(enemyBody);
            this.scene.matter.applyForce(playerBody, { x: 0, y: -0.05 });
            this.scene.cameras.main.shake(80, 0.003);
        } else {
            this.triggerPlayerDeath();
        }
    }

    /**
     * Kill an enemy
     * @param {Object} enemyBody - Enemy physics body
     */
    killEnemy(enemyBody) {
        if (enemyBody.gameObject && enemyBody.gameObject.enemyObject) {
            this.createDeathParticles(enemyBody.position.x, enemyBody.position.y, 0xff0000);
            enemyBody.gameObject.enemyObject.kill();
        } else if (enemyBody.gameObject) {
            enemyBody.gameObject.destroy();
        }
        
        // Update enemies array - support both sprite and physicsSprite references
        if (this.enemies) {
            this.enemies = this.enemies.filter(e => {
                const activeSprite = e.sprite || e.physicsSprite;
                return activeSprite && activeSprite.active;
            });
        }
        
        if (this.onEnemyKill) {
            this.onEnemyKill(enemyBody);
        }
        
        eventBus.emit(GameEvents.ENEMY_KILLED);
    }

    /**
     * Handle star collection
     * @param {Object} starBody - Star physics body
     */
    handleStarCollision(starBody) {
        if (starBody.gameObject) {
            this.createCollectParticles(starBody.position.x, starBody.position.y);
            starBody.gameObject.destroy();
        }
        this.scene.matter.world.remove(starBody);
        
        if (this.onStarCollect) {
            this.onStarCollect(starBody);
        }
        
        this.scene.cameras.main.flash(100, 255, 215, 0, false);
    }

    /**
     * Trigger player death
     */
    triggerPlayerDeath() {
        if (this.isGameOver) return;
        this.isGameOver = true;
        
        if (this.onPlayerDeath) {
            this.onPlayerDeath();
        }
    }

    /**
     * Create death particle effect
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} color - Particle color
     */
    createDeathParticles(x, y, color) {
        for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            const speed = Phaser.Math.Between(50, 100);
            const particle = this.scene.add.rectangle(
                x, y,
                Phaser.Math.Between(6, 14),
                Phaser.Math.Between(6, 14),
                color
            );

            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * speed,
                y: y + Math.sin(angle) * speed + 50,
                alpha: 0,
                rotation: Phaser.Math.Between(-4, 4),
                duration: 700,
                ease: 'Power2',
                onComplete: () => particle.destroy()
            });
        }
    }

    /**
     * Create star collect particle effect
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createCollectParticles(x, y) {
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const particle = this.scene.add.star(x, y, 5, 3, 8, 0xFFD700);

            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * 60,
                y: y + Math.sin(angle) * 60,
                alpha: 0,
                scale: 0.3,
                rotation: Math.PI * 2,
                duration: 500,
                ease: 'Power2',
                onComplete: () => particle.destroy()
            });
        }

        const burst = this.scene.add.circle(x, y, 5, 0xffffff);
        this.scene.tweens.add({
            targets: burst,
            scale: 4,
            alpha: 0,
            duration: 300,
            onComplete: () => burst.destroy()
        });
    }

    /**
     * Set door open state
     * @param {boolean} open - Whether door is open
     */
    setDoorOpen(open) {
        this.doorOpen = open;
    }

    /**
     * Reset game over state
     */
    reset() {
        this.isGameOver = false;
        this.doorOpen = false;
    }

    /**
     * Cleanup
     */
    destroy() {
        this.scene.matter.world.off('collisionstart', this.handleCollisionStart, this);
        this.scene.matter.world.off('collisionactive', this.handleCollisionActive, this);
        this.systems = [];
        this.player = null;
        this.levelBuilder = null;
    }
}
